import React from 'react'
import { Box, Text, Center } from '@chakra-ui/react'

import {
    AppointmentFieldsFragment,
    ChatClinicFieldsFragment,
    ClientPreviewFieldsFragment,
    MessageTemplateFieldsFragment,
} from '~graphql/generated/graphql'
import { PatientIdAndName } from '../types'
import MessageTemplateOption from './option'
import Pagination from '~components/shared/pagination'
import SharedStack from '~components/shared/wrappers/shared-stack'
import PageActionBar from '~components/shared/page-layout/action-bar'
import SharedSectionHeader from '~components/shared/texts/shared-section-header'
import { useMessageTemplates } from '~components/engagement/message-templates/provider'
import SortingSelect from '~components/shared/actions/sorting-select'

interface Props {
    clinic: ChatClinicFieldsFragment
    client: ClientPreviewFieldsFragment
    selectedPatient: PatientIdAndName | undefined
    appointment?: AppointmentFieldsFragment | undefined
    clientPreviousDoctor?: string | undefined
    patientPreviousDoctor?: string | undefined
    googleCalendarEvent?: string | undefined
    selectedTemplate: MessageTemplateFieldsFragment | undefined
    setSelectedTemplate: (
        template: MessageTemplateFieldsFragment | undefined,
    ) => void
}

export default function MessageTemplatesList({
    clinic,
    client,
    selectedPatient,
    appointment,
    clientPreviousDoctor,
    patientPreviousDoctor,
    googleCalendarEvent,
    selectedTemplate,
    setSelectedTemplate,
}: Props) {
    const {
        templates,
        totalTemplates,
        query,
        setQuery,
        debouncedQuery,
        orderBy,
        setOrderBy,
        page,
        setPage,
        pageSize,
    } = useMessageTemplates()

    return (
        <SharedStack spacing={{ base: 3, md: 4 }}>
            <Box w="full">
                <SharedSectionHeader>Select a template</SharedSectionHeader>
                <PageActionBar
                    search={{
                        placeholder: 'Search templates by name...',
                        query,
                        setQuery,
                    }}
                    actions={[
                        <SortingSelect<Record<string, unknown>>
                            value={{
                                field: orderBy.column,
                                isAscending: !!orderBy.isAscending,
                            }}
                            setValue={({ field, isAscending }) =>
                                setOrderBy({ column: field, isAscending })
                            }
                            options={[
                                {
                                    label: 'Name',
                                    field: 'name',
                                    isAscending: true,
                                    ascendingLabel: (
                                        <Text
                                            fontSize="xs"
                                            fontWeight="semibold"
                                        >
                                            (A-Z)
                                        </Text>
                                    ),
                                },
                                {
                                    label: 'Name',
                                    field: 'name',
                                    isAscending: false,
                                    ascendingLabel: (
                                        <Text
                                            fontSize="xs"
                                            fontWeight="semibold"
                                        >
                                            (Z-A)
                                        </Text>
                                    ),
                                },
                                {
                                    label: 'Created At',
                                    field: 'created_at',
                                    isAscending: true,
                                },
                                {
                                    label: 'Created At',
                                    field: 'created_at',
                                    isAscending: false,
                                },
                            ]}
                        />,
                    ]}
                    gridOverrides={{
                        mt: 2,
                        w: 'full',
                    }}
                />
            </Box>
            {templates.map(template => (
                <MessageTemplateOption
                    key={template.id}
                    clinic={clinic}
                    client={client}
                    template={template}
                    patient={selectedPatient}
                    appointment={appointment}
                    clientPreviousDoctor={clientPreviousDoctor}
                    patientPreviousDoctor={patientPreviousDoctor}
                    googleCalendarEvent={googleCalendarEvent}
                    isSelected={selectedTemplate?.id === template.id}
                    onClick={() => {
                        selectedTemplate?.id === template.id
                            ? setSelectedTemplate(undefined)
                            : setSelectedTemplate(template)
                    }}
                    query={debouncedQuery}
                />
            ))}
            {totalTemplates > pageSize && (
                <Center w="full">
                    <Pagination
                        page={page}
                        setPage={setPage}
                        pageSize={pageSize}
                        count={totalTemplates}
                    />
                </Center>
            )}
        </SharedStack>
    )
}
